import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  type Over,
} from '@dnd-kit/core';
import * as React from 'react';

import { Box } from '../../components/ui/box';
import { DND_DRAGGABLE_PROPS, DND_OVERLAY_PROPS } from '../../lib/dnd-attrs';
import { GRID_AUTO_SCROLL } from './sortable-grid';
import { omitUndefined, type Omittable } from "../../lib/props";

// One shared DndContext for a builder surface: `GridPaletteItem`s and any
// `SortableGrid` rendered beneath the scope participate in the same drag
// space, so a palette card can be dragged straight onto the grid. A
// SortableGrid detects the scope via context and registers its drag handlers
// here instead of mounting its own DndContext; without an ancestor scope it
// behaves exactly as before (dashboards untouched).

export interface GridDndHandlers {
  onDragStart: (event: DragStartEvent) => void;
  onDragMove: (event: DragMoveEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: (event: DragCancelEvent) => void;
}

interface GridDndScopeContextValue {
  /** Register a grid's drag handlers; returns the unregister cleanup. */
  registerGrid: (handlers: GridDndHandlers) => () => void;
  /** Palette items publish their "lifted" clone here while dragging so the
   *  scope's single `DragOverlay` renders it. The overlay is portaled above
   *  every panel and scroll clip, so the preview can never fall behind the
   *  canvas or get cut off by the palette's own scroll container — the failure
   *  an in-place `translate` + `zIndex` could not escape. */
  setOverlay: (node: React.ReactNode) => void;
}

export const GridDndScopeContext =
  React.createContext<GridDndScopeContextValue | null>(null);

/** True while ANY drag under the scope is in flight. A separate context from
 *  the scope's registration/overlay channel so the per-drag re-render touches
 *  only its consumers (footers, seams), not every palette card. */
const GridDragActiveContext = React.createContext(false);

/** Lifecycle subscription for non-gesture components (quick-add footers,
 *  insertion seams) that hide while a drag is in flight anywhere under the
 *  scope. False outside a `SortableGridDndScope`. */
export function useGridDragActive(): boolean {
  return React.useContext(GridDragActiveContext);
}

/** Drag payload key marking a drag as an external palette insertion. */
const EXTERNAL_DATA_KEY = 'gridPaletteItem';

/** Drag payload key on a grid tile carrying a consumer payload
 *  (`SortableGridItem externalItem`). The payload lets that tile's drag ride
 *  OTHER grids' external phantom path — a page tile dropping into a nested
 *  columns grid, a nested child dragging out to the page. The drop CELL
 *  carries all positional information; there is no index/zone bookkeeping. */
export const GRID_TILE_ITEM_KEY = 'gridTileItem';

/** Marker key on subgrid-zone droppables (a nested grid's own drop band) —
 *  `subgridZoneOver` keys off it. */
export const GRID_SUBGRID_ZONE_KEY = 'gridSubgridZone';

type ActiveLike = {
  active: { data: { current?: Record<string, unknown> | undefined } };
};

/** True for drags carrying a payload that can mint/move a tile from OUTSIDE a
 *  grid's own layout: palette insertions and payload-carrying tiles. The
 *  receiving grid neither knows nor cares which kind produced the payload.
 *  NOTE: routing is id-ownership-first — a grid that owns the active id treats
 *  the drag as internal even when it carries a payload. */
export function isExternalGridDrag(event: ActiveLike): boolean {
  const data = event.active.data.current;
  return (
    data != null && (EXTERNAL_DATA_KEY in data || GRID_TILE_ITEM_KEY in data)
  );
}

export function externalGridDragItem(event: ActiveLike): unknown {
  const data = event.active.data.current;
  if (data && GRID_TILE_ITEM_KEY in data) return data[GRID_TILE_ITEM_KEY];
  return data?.[EXTERNAL_DATA_KEY];
}

/** The subgrid-zone id the drag's `over` currently points at, else null.
 *  The symmetric preview rule keys on it: a grid previews iff
 *  `subgridZoneOver(over) === core.zoneId` — for page grids (zoneId null)
 *  that means "not over any enabled zone"; for nested grids, only while over
 *  their own zone. Drop ownership follows the same identity: exactly one grid
 *  commits. */
export function subgridZoneOver(over: Over | null | undefined): string | null {
  return over?.data.current?.[GRID_SUBGRID_ZONE_KEY] === true
    ? String(over.id)
    : null;
}

export interface SortableGridDndScopeProps {
  children: React.ReactNode;
}

export function SortableGridDndScope({ children }: SortableGridDndScopeProps) {
  // Same sensor tuning as a standalone SortableGrid: 4px mouse activation
  // keeps click-to-add unambiguous from drag-to-place on palette cards.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const gridsRef = React.useRef(new Set<GridDndHandlers>());
  const registerGrid = React.useCallback((handlers: GridDndHandlers) => {
    gridsRef.current.add(handlers);
    return () => {
      gridsRef.current.delete(handlers);
    };
  }, []);

  // The lifted clone the active palette card publishes for the overlay. Null
  // whenever no palette drag is in flight.
  const [overlay, setOverlay] = React.useState<React.ReactNode>(null);
  // The drag-active lifecycle signal `useGridDragActive` consumers read.
  const [dragActive, setDragActive] = React.useState(false);
  const ctx = React.useMemo(
    () => ({ registerGrid, setOverlay }),
    [registerGrid],
  );

  // Every registered grid sees every event; each grid ignores drags that are
  // not its own (internal drags by layout id, external drags by pointer rect).
  const onDragStart = React.useCallback((event: DragStartEvent) => {
    setDragActive(true);
    for (const grid of gridsRef.current) grid.onDragStart(event);
  }, []);
  const onDragMove = React.useCallback((event: DragMoveEvent) => {
    for (const grid of gridsRef.current) grid.onDragMove(event);
  }, []);
  const onDragEnd = React.useCallback((event: DragEndEvent) => {
    for (const grid of gridsRef.current) grid.onDragEnd(event);
    setOverlay(null);
    setDragActive(false);
  }, []);
  const onDragCancel = React.useCallback((event: DragCancelEvent) => {
    for (const grid of gridsRef.current) grid.onDragCancel(event);
    setOverlay(null);
    setDragActive(false);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      autoScroll={GRID_AUTO_SCROLL}
      // Subgrid zones are the scope's only droppables. `pointerWithin` gives
      // "the pointer is inside the zone" semantics (rect intersection would
      // light zones the dragged tile merely overlaps); `Always` re-measures
      // because zone rects move mid-drag (auto-scroll, FLIP reflow, and
      // containers that live-grow while hovered).
      collisionDetection={pointerWithin}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <GridDndScopeContext.Provider value={ctx}>
        <GridDragActiveContext.Provider value={dragActive}>
          {children}
        </GridDragActiveContext.Provider>
      </GridDndScopeContext.Provider>
      {/* Portaled preview: the ONE drag ghost for palette insertions. Lives
       *  above every panel/scroll container, so it can't render behind the
       *  canvas. No drop animation — on release the real tile appears in the
       *  grid, so a fly-back to the palette would read as a bounce. */}
      <DragOverlay dropAnimation={null}>
        {overlay ? (
          <Box {...DND_OVERLAY_PROPS} cursor="grabbing">
            {overlay}
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export interface GridPaletteItemRenderArgs {
  isDragging: boolean;
}

export type GridPaletteItemProps = Omittable<{
  /** Unique id within the scope (e.g. the palette entry key). */
  id: string;
  /** Opaque payload handed to the grid's `onExternalDrop`/`externalSpan`. */
  item: unknown;
  disabled?: boolean;
  children:
    | React.ReactNode
    | ((args: GridPaletteItemRenderArgs) => React.ReactNode);
}>;

/**
 * Makes a palette card draggable into any grid under the same
 * `SortableGridDndScope`. The card keeps its slot in the palette list and dims
 * while dragging; the pointer-following preview is the scope's `DragOverlay`
 * (portaled above every panel/scroll clip), NOT a transform on this element.
 * On drop the grid fires `onExternalDrop` with this item's payload.
 */
export function GridPaletteItem({
  id,
  item,
  disabled,
  children,
}: GridPaletteItemProps) {
  const scope = React.useContext(GridDndScopeContext);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable(
    omitUndefined({
      id: `palette:${id}`,
      data: { [EXTERNAL_DATA_KEY]: item },
      disabled,
    }),
  );

  // The clone the scope's DragOverlay renders while this card is the active
  // drag. Held in a ref so a re-render mid-drag never tears the overlay down.
  const overlayContent =
    typeof children === 'function' ? children({ isDragging: true }) : children;
  const overlayRef = React.useRef(overlayContent);
  overlayRef.current = overlayContent;
  React.useLayoutEffect(() => {
    if (!isDragging || !scope) return;
    scope.setOverlay(overlayRef.current);
    return () => scope.setOverlay(null);
  }, [isDragging, scope]);

  return (
    <Box
      ref={setNodeRef}
      // The source stays anchored in the palette and dims — the DragOverlay
      // clone follows the pointer. Translating this element too would move the
      // ghost at double speed.
      className={isDragging ? 'opacity-40' : undefined}
      cursor={disabled ? undefined : isDragging ? 'grabbing' : 'grab'}
      {...(disabled ? {} : DND_DRAGGABLE_PROPS)}
      {...(disabled ? {} : (attributes as unknown as Record<string, unknown>))}
      {...(disabled ? {} : (listeners as unknown as Record<string, unknown>))}
    >
      {typeof children === 'function' ? children({ isDragging }) : children}
    </Box>
  );
}
